<script lang="ts">
  import { onDestroy, onMount } from 'svelte';
  import { T, useFrame } from '@threlte/core';
  import { interactivity } from '@threlte/extras';
  import fragmentShader from './material/shaders/TimeCrystal.frag';
  import vertexShader from './material/shaders/TimeCrystal.vert';
  import {
    FrontSide,
    CubeCamera,
    CubeTextureLoader,
    TextureLoader,
    WebGLCubeRenderTarget,
    LinearMipmapLinearFilter,
    SRGBColorSpace,
    RGBAFormat,
    ShaderMaterial
  } from 'three';
  import ChamferedCubeGeometry from './ChamferedCubeGeometry';
  import { BaseColor } from '../GradientStore';
  import { bpm, PlayState, STATE } from '../RecordStore';
  import { tweened } from 'svelte/motion';
  import { sineInOut, linear } from 'svelte/easing';
  import matCap from './material/textures-minified/matcap.webp';
  import tex1 from './material/textures-minified/tex1.webp';
  import tex2 from './material/textures-minified/tex2.webp';
  import tex3 from './material/textures-minified/tex3.webp';
  import tex4 from './material/textures-minified/tex4.webp';
  import tex5 from './material/textures-minified/tex5.webp';
  import tex6 from './material/textures-minified/tex6.webp';

  import { accessibilityPreferences } from '$lib/stores/accessibility';

  const chamferedCube = ChamferedCubeGeometry(1, 0.004, 16);

  const cubeTexturePaths = [tex1, tex2, tex3, tex4, tex5, tex6];
  let cubeRenderTarget: WebGLCubeRenderTarget;
  let cubeCamera: CubeCamera;
  let rotationY = 0;
  let spin = 0;

  const renderTargetConfig = {
    format: RGBAFormat,
    generateMipmaps: true,
    minFilter: LinearMipmapLinearFilter,
    colorSpace: SRGBColorSpace
  };

  new CubeTextureLoader().load(cubeTexturePaths, (baseTexture) => {
    uniforms.baseTexture.value = baseTexture;
  });

  new TextureLoader().load(matCap, (matcap) => {
    uniforms.matCapText.value = matcap;
  });

  useFrame(({ renderer, scene }) => {
    if (renderer && cubeCamera) {
      cubeCamera.update(renderer, scene);
      uniforms.env.value = cubeRenderTarget.texture;
      uniforms.twistDirection = { value: [-1, 0, 0] };
    }
    if (!$accessibilityPreferences.reduceMotion) {
      rotationY += spin;
      uniforms.twistDirection = { value: $twistDirection };
    }
  });

  let uniforms: any = {
    baseTexture: { value: null },
    matCapText: { value: null },
    env: { value: null },
    twistDirection: { value: [1.0, 0.0, 0.0] },
    baseColor: { value: $BaseColor }
  };

  // setup tweened value for baseColor
  const tweenedBaseColor = tweened($BaseColor, {
    duration: 2500,
    easing: sineInOut
  });
  //tween base color when value changes
  $: tweenedBaseColor.set($BaseColor);
  $: uniforms.baseColor = { value: $tweenedBaseColor };

  //setup tweened value for twist direction
  const twistDirection = tweened([-1, 0, 0], {
    duration: 1000,
    easing: sineInOut
  });

  const voguing = async () => {
    const props = {
      duration: ($bpm / 60) * 800,
      delay: ($bpm / 60) * 530,
      easing: sineInOut
    };
    await twistDirection.set([0, 0, -1], props);
    await twistDirection.set([0, 1, 0], props);
    await twistDirection.set([-1, 0, 0], props);
    loopComplete();
  };
  const grooving = async () => {
    const props = {
      duration: ($bpm / 60) * 750,
      delay: 0,
      easing: linear
    };
    await twistDirection.set([0, 0, -1], props);
    await twistDirection.set([1, 0, 0], props);
    await twistDirection.set([0, 0, 1], props);
    await twistDirection.set([-1, 0, 0], props);
    loopComplete();
  };

  const loopComplete = () => {
    spin = -0.002;
    if ($PlayState == STATE.PLAYING) {
      grooving();
    } else {
      voguing();
    }
  };

  onMount(() => {
    setTimeout(voguing, 6000);
    document.body.addEventListener('mouseup', onPointerUp);
  });

  onDestroy(() => {
    document.body.removeEventListener('mouseup', onPointerUp);
  });

  //pointer management
  interactivity();
  let hover = false;
  let grabbing = false;
  const onPointerEnter = (e: any) => {
    e.stopPropagation();
    hover = true;
    updateCursor();
  };
  const onPointerLeave = (e: any) => {
    e.stopPropagation();
    hover = false;
    updateCursor();
  };
  const onPointerDown = (e: any) => {
    e.stopPropagation();
    grabbing = true;
    updateCursor();
  };
  const onPointerUp = (e: any) => {
    e.stopPropagation();
    grabbing = false;
    updateCursor();
  };
  const updateCursor = () => {
    if (grabbing) {
      document.body.style.cursor = 'grabbing';
    } else if (hover) {
      document.body.style.cursor = 'grab';
    } else {
      document.body.style.cursor = '';
    }
  };
</script>

<T.WebGLCubeRenderTarget
  bind:ref={cubeRenderTarget}
  args={[256, renderTargetConfig]}
  dispose={false}
>
  <T.CubeCamera bind:ref={cubeCamera} args={[0.2, 40, cubeRenderTarget]} />
</T.WebGLCubeRenderTarget>
<T.Mesh
  rotation.y={rotationY}
  on:pointerenter={onPointerEnter}
  on:pointerleave={onPointerLeave}
  on:pointerdown={onPointerDown}
  on:pointerup={onPointerUp}
  geometry={chamferedCube}
  material={new ShaderMaterial({
    uniforms,
    vertexShader,
    fragmentShader,
    side: FrontSide
  })}
/>
